'use client';

import React, { useState } from 'react';
import Button from '@/ui/components/shared/Button';
import axios from 'axios';
import { apiPaths } from '@/constants/apiPaths';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import { useTranslations } from 'next-intl';
import { useNukiDevices } from '@/ui/providers/nukiDevicesProvider';
import { useLocalization } from '@/ui/providers/localizationProvider';
import useUser from '@/ui/providers/userProvider';
import { DataLayerHelper } from '@/utils/data-layer-helper';
import { readUserMetaCurrentCookie } from '@/utils/cookie-helper';
import { buildTrackingId } from '@/utils/tracking-helper';
import PrivacyPolicyCheckBox from '../PrivacyPolicyCheckBox';
import { ScreenToShowState } from '../../types/ScreenToShowState';

interface LoggenInFormProps {
  setScreenToShow: (screen: ScreenToShowState) => void;
  existingCustomerEmail: string;
  buttonText?: string;
  onExistingUserJoinClub?: () => void;
  // Where the signup came from; wins over the nag the visitor's tracking cookie carries.
  nag?: string;
  trackingPrefix?: string;
}

export function LoggedInForm({
  setScreenToShow,
  existingCustomerEmail,
  buttonText,
  onExistingUserJoinClub,
  nag,
  trackingPrefix,
}: Readonly<LoggenInFormProps>) {
  const { language, country } = useLocalization();
  const t = useTranslations('ClubRegistration.LoggedInForm');
  const { nukiDevices } = useNukiDevices();
  const { refreshSessionAndCustomer } = useUser();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const validationSchema = yup.object().shape({
    privacyPolicy: yup
      .boolean()
      .oneOf([true], t('You must accept the privacy policy'))
      .required(t('You must accept the privacy policy')),
  });

  const {
    register,
    trigger,
    formState: { errors },
  } = useForm<{
    privacyPolicy: boolean;
  }>({
    resolver: yupResolver(validationSchema),
    defaultValues: {
      privacyPolicy: false,
    },
  });

  const handleSubmit = async () => {
    const isValid = await trigger();
    if (!isValid) {
      return;
    }

    try {
      setLoading(true);
      setError(false);
      const umc = readUserMetaCurrentCookie();
      const searchParams = new URLSearchParams({
        email: existingCustomerEmail,
        nukiDevices: nukiDevices || '',
        language,
        country,
      });

      if (umc?.source) {
        searchParams.append('utmSource', umc.source);
      }

      if (umc?.medium) {
        searchParams.append('utmMedium', umc.medium);
      }

      if (umc?.campaignName) {
        searchParams.append('utmCampaign', umc.campaignName);
      }

      if (umc?.content) {
        searchParams.append('utmContent', umc.content);
      }

      const signupNag = nag ?? umc?.nag;
      if (signupNag) {
        searchParams.append('nag', signupNag);
      }

      const response = await axios.post(
        apiPaths.customers.nukiClubSignup + searchParams.toString(),
      );

      if (response.status === 200) {
        DataLayerHelper.pushSignUp('email');
        // The provider's customer cache went stale the moment the join succeeded —
        // refresh it here so the welcome screen CTA and header see the membership.
        refreshSessionAndCustomer();
        setScreenToShow(ScreenToShowState.WELCOME);
        if (onExistingUserJoinClub) {
          onExistingUserJoinClub();
        }
      }
    } catch (submitError) {
      setError(true);
      console.error(submitError);
    } finally {
      setLoading(false);
    }
  };

  const resolvedButtonText = buttonText || t('Become a member for free');
  const submitButtonTrackingId = buildTrackingId(
    trackingPrefix,
    'logged-in-form',
    resolvedButtonText,
  );

  return (
    <div>
      <p className="mx-auto max-w-lg text-center text-md font-bold">
        {t('To join the Nuki Club, please agree to our privacy policy:')}
      </p>
      <form className="mx-auto mt-6 max-w-sm space-y-4">
        <PrivacyPolicyCheckBox register={register} errors={errors.privacyPolicy} />
        <Button
          type="button"
          className="w-full"
          loading={loading}
          onClick={handleSubmit}
          data-tile-element-id={submitButtonTrackingId}
        >
          {resolvedButtonText}
        </Button>
        {error && (
          <p role="alert" className="text-center text-sm text-red">
            {t('There was an error signing you up Please try again')}
          </p>
        )}
      </form>
    </div>
  );
}
